/**
 * snapshotStorage.ts — Read/write versioned encrypted snapshot files on-device
 * and in iCloud, with live checksum-verified backup status per location.
 *
 * Storage paths:
 *   Device (internal, not browsed by user):
 *     ${FileSystem.documentDirectory}snapshots/project_{id}/snapshot_v{N}.enc
 *
 *   iCloud (surfaced in Files.app → iCloud Drive → Eleplan):
 *     Vault/README.txt                                           — plain-English explainer
 *     Vault/{planLabel}/Snapshot v{N} — {Mon D, YYYY}.eleplan    — encrypted stored copy
 *
 *   A legacy iCloud layout of `snapshots/project_{id}/snapshot_v{N}.enc` is
 *   still read for backward compatibility so users who backed up before the
 *   rename don't see their status flip to "missing" on first launch.
 *
 * Each .eleplan / .enc file is a JSON envelope containing the AES-256-GCM
 * ciphertext, IV, and auth tag produced by snapshotCrypto.ts — no plaintext
 * PHI on disk.
 *
 * iCloud:
 *   Uses @oleg_svetlichnyi/expo-icloud-storage for iCloud Drive file operations.
 *   Entitlements + NSUbiquitousContainers are applied by that package's Expo
 *   config plugin (wired into app.json). The iCloud container identifier is
 *   iCloud.com.eleplan.app.
 *
 *   The container is declared "public scope"
 *   (NSUbiquitousContainerIsDocumentScopePublic = true) which makes it show
 *   up as a top-level Eleplan folder inside Files.app → iCloud Drive. Users
 *   see their encrypted snapshot files there. They can't decrypt anything
 *   without the app, but the presence of the folder is intentional — it's
 *   the "your data is really there, you own it" proof we want users to feel.
 *
 *   For users signed into iCloud with iCloud Drive enabled (the default on
 *   every iPhone), iCloud operations work with zero runtime permission prompts
 *   — iOS treats the app's ubiquity container as an implicit capability.
 *   `isICloudAvailableAsync` returns false only if the user is signed out of
 *   iCloud or has disabled iCloud Drive system-wide; the UI surfaces that as
 *   an informational note rather than a permission request.
 */

import * as FileSystem from 'expo-file-system/legacy';
import * as Crypto from 'expo-crypto';
import { Platform } from 'react-native';
import type { SnapshotEncryptedBlob } from './snapshotCrypto';
import debug from './debug';
import {
  saveBlob as webSaveBlob,
  loadBlob as webLoadBlob,
  blobInfo as webBlobInfo,
  deleteBlob as webDeleteBlob,
  listVersions as webListVersions,
} from './snapshotBlobStore';

// Web (Electron renderer + browser) has no usable expo-file-system on the
// device side — its stub is a no-op set of resolves. We keep the native path
// in the rest of this file unchanged and divert the device-storage entry
// points to an IndexedDB-backed blob store so backup / open / status work
// end-to-end on desktop the same way they do on iPhone.
const isWeb = Platform.OS === 'web';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StorageLocation = 'device' | 'icloud';

/**
 * Per-location status codes. Precedence for the hero card, high-to-low:
 *
 *   verified > outdated > stale > missing > unavailable > checking
 *
 *   verified    — this location has the latest version AND its bytes match the
 *                 registered checksum.
 *   outdated    — this location doesn't have the latest, but DOES have a
 *                 verified older version. The user's plan is still recoverable
 *                 from here; they just need a fresher copy.
 *   stale       — the file at the path for the latest version exists but its
 *                 checksum doesn't match. Corruption signal.
 *   missing     — no verified copy at this location (never wrote, or wiped).
 *   unavailable — location is not reachable (iCloud off, Android, etc).
 *   checking    — still being probed.
 */
export type BackupStatusCode =
  | 'verified'
  | 'outdated'
  | 'stale'
  | 'missing'
  | 'checking'
  | 'unavailable';

export interface BackupStatus {
  location: StorageLocation;
  status: BackupStatusCode;
  checkedAt: string;
  sizeBytes?: number;
  /** Highest version this location has a verified copy of, if any. */
  availableVersion?: number | null;
  /** The latest known registered version (for comparison in the UI). */
  targetVersion?: number | null;
}

/**
 * A snapshot version entry from the server registry, as used by the status
 * scanner. This mirrors the fields returned by GET /snapshots/versions and
 * is all the scanner needs to decide whether a stored blob is "the latest"
 * or "an older-but-still-valid" copy.
 */
export interface SnapshotRegistryEntry {
  version: number;
  checksum: string;
  /** ISO timestamp — used to reconstruct the iCloud human filename. */
  created_at?: string | null;
}

export interface SnapshotFileEnvelope extends SnapshotEncryptedBlob {
  schema_version: number;
  snapshot_version: number;
  created_at: string;
}

// ─── Paths ────────────────────────────────────────────────────────────────────

const deviceSnapshotDir = (projectId: number | string): string =>
  `${FileSystem.documentDirectory}snapshots/project_${projectId}/`;

const deviceSnapshotPath = (projectId: number | string, version: number): string =>
  `${deviceSnapshotDir(projectId)}snapshot_v${version}.enc`;

// ─── iCloud filename scheme ───────────────────────────────────────────────────
// Files surface in the user's iCloud Drive under Eleplan > Vault. Names are
// intentionally human-readable ("Snapshot v3 — Apr 17, 2026.eleplan") so the
// folder reads as proof-of-backup, not engineer directory listing.
//
// Two points of care:
//   1. The filename embeds the snapshot's created_at so we can derive the
//      exact same path at read time without persisting a mapping table.
//   2. We keep the legacy (`snapshots/project_{id}/snapshot_v{N}.enc`) layout
//      readable for backward compatibility — if a user upgraded and already
//      had snapshots in the old path, we still find them.

const VAULT_ROOT = 'Vault';
const VAULT_README_RELATIVE = `${VAULT_ROOT}/README.txt`;
const VAULT_FILE_EXT = '.eleplan';

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatVaultDate(iso?: string | null): string {
  if (!iso) return 'Unknown Date';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Unknown Date';
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Sanitize a user-facing plan label for use as a folder name. Strips characters
 * that upset iOS / macOS / iCloud sync, collapses whitespace, and falls back to
 * `Plan {projectId}` if the label is empty or becomes empty after sanitization.
 */
function sanitizePlanLabel(label: string | undefined | null, projectId: number | string): string {
  const fallback = `Plan ${projectId}`;
  if (!label) return fallback;
  const cleaned = String(label)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^\.+|\.+$/g, '')
    .trim();
  return cleaned.length ? cleaned : fallback;
}

const icloudVaultDir = (planLabel: string): string =>
  `${VAULT_ROOT}/${planLabel}`;

const humanSnapshotFilename = (version: number, createdAt?: string | null): string =>
  `Snapshot v${version} — ${formatVaultDate(createdAt)}${VAULT_FILE_EXT}`;

const icloudVaultSnapshotPath = (planLabel: string, version: number, createdAt?: string | null): string =>
  `${icloudVaultDir(planLabel)}/${humanSnapshotFilename(version, createdAt)}`;

/** Legacy iCloud paths (pre-rename). Still read for backward compatibility. */
const icloudLegacySnapshotPath = (projectId: number | string, version: number): string =>
  `snapshots/project_${projectId}/snapshot_v${version}.enc`;

const icloudLegacySnapshotDir = (projectId: number | string): string =>
  `snapshots/project_${projectId}`;

// ─── iCloud README ────────────────────────────────────────────────────────────
// Written once at the container root the first time we sync to iCloud. Gives
// users who poke around Files.app a plain-English explanation of what they're
// looking at, without them having to open the app to understand.

const VAULT_README_CONTENT = `Eleplan Vault
=============

These files are your encrypted Eleplan health vault.

You can see them here to prove your data is safely backed up to iCloud,
but they can only be read inside the Eleplan app, on a trusted device,
after you pass a biometric check. No one — not us, not Apple, not
someone with this file — can decrypt them without your device's key.

What's in this folder
---------------------
- Each "Snapshot vN — <date>.eleplan" file is a versioned AES-256-GCM
  encrypted copy of your health record at that point in time.
- Higher version numbers are newer.
- Plain health information (names, medications, notes, etc.) is never
  written here. Only encrypted ciphertext.

What to do with these files
---------------------------
- Leave them here. iCloud keeps them in sync across your devices so you
  always have a backup.
- Don't rename or move them — the app uses these names to find the right
  snapshot.
- If you delete one, it just means that version is gone from iCloud. The
  app will create a fresh snapshot the next time you back up.

Your data, in your hands.

— Eleplan
`;

async function writeVaultReadmeIfMissing(icloud: ICloudModule): Promise<void> {
  try {
    const exists = await icloud.isExistAsync(VAULT_README_RELATIVE, false).catch(() => false);
    if (exists) return;

    // The iCloud bridge uploads from a local file path, so we stage the README
    // in the app's cache dir first, then upload it into the Vault root.
    const tempDir = `${FileSystem.cacheDirectory}snapshot_vault_staging/`;
    await ensureDir(tempDir);
    const stagedPath = `${tempDir}README.txt`;
    await FileSystem.writeAsStringAsync(stagedPath, VAULT_README_CONTENT, {
      encoding: FileSystem.EncodingType.UTF8,
    });

    try {
      await icloud.createDirAsync(VAULT_ROOT).catch(() => false);
      await icloud.uploadFileAsync({
        destinationPath: VAULT_README_RELATIVE,
        filePath: stagedPath,
      });
    } finally {
      await FileSystem.deleteAsync(stagedPath, { idempotent: true }).catch(() => {});
    }
  } catch (err: any) {
    debug.warn('Failed to write Vault README.txt', err?.message);
  }
}

// ─── Device storage ───────────────────────────────────────────────────────────

async function ensureDir(path: string): Promise<void> {
  const info = await FileSystem.getInfoAsync(path);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(path, { intermediates: true });
  }
}

/**
 * Checksum formula must match backend/controllers/snapshots/create-snapshot/index.js:
 * sha256(encrypted_data_hex + iv_hex + auth_tag_hex).
 * Metadata fields (version, timestamps) are deliberately excluded so checksum
 * equality reflects ciphertext integrity, not envelope shape.
 *
 * Exported so callers (e.g. openSnapshotView) can verify a blob's integrity
 * against the server-registered checksum before attempting to decrypt it.
 */
export async function envelopeChecksum(envelope: SnapshotEncryptedBlob): Promise<string> {
  const concat = envelope.encrypted_data + envelope.iv + envelope.auth_tag;
  return Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, concat);
}

/**
 * Write an encrypted snapshot envelope to durable on-device storage.
 *
 * Native: writes a JSON file under `${documentDirectory}snapshots/project_{id}/`
 * via expo-file-system.
 * Web (Electron renderer + browser): persists into the per-origin IndexedDB
 * snapshot store (see `snapshotBlobStore.ts`). In Electron the IDB data lives
 * inside `userData/IndexedDB/`, giving the same "your machine, your data"
 * durability as a flat file on disk.
 *
 * Returns the SHA-256 checksum of the ciphertext and on-disk byte size.
 */
export async function saveSnapshot(
  projectId: number | string,
  version: number,
  envelope: SnapshotFileEnvelope,
): Promise<{ checksum: string; sizeBytes: number }> {
  const content = JSON.stringify(envelope);
  if (isWeb) {
    const { sizeBytes } = await webSaveBlob(projectId, version, content);
    const checksum = await envelopeChecksum(envelope);
    return { checksum, sizeBytes };
  }
  await ensureDir(deviceSnapshotDir(projectId));
  const path = deviceSnapshotPath(projectId, version);
  await FileSystem.writeAsStringAsync(path, content, { encoding: FileSystem.EncodingType.UTF8 });
  const checksum = await envelopeChecksum(envelope);
  return { checksum, sizeBytes: new TextEncoder().encode(content).length };
}

/**
 * Load an encrypted snapshot envelope. Returns null if the snapshot is not
 * present on this device. See `saveSnapshot` for the per-platform backend.
 */
export async function loadSnapshot(
  projectId: number | string,
  version: number,
): Promise<SnapshotFileEnvelope | null> {
  if (isWeb) {
    const content = await webLoadBlob(projectId, version);
    if (!content) return null;
    try {
      return JSON.parse(content) as SnapshotFileEnvelope;
    } catch {
      return null;
    }
  }
  const path = deviceSnapshotPath(projectId, version);
  const info = await FileSystem.getInfoAsync(path);
  if (!info.exists) return null;
  try {
    const content = await FileSystem.readAsStringAsync(path, { encoding: FileSystem.EncodingType.UTF8 });
    return JSON.parse(content) as SnapshotFileEnvelope;
  } catch {
    return null;
  }
}

/**
 * List all snapshot versions stored on the device for a project, descending.
 */
export async function listLocalSnapshotVersions(projectId: number | string): Promise<number[]> {
  if (isWeb) {
    return webListVersions(projectId);
  }
  const dir = deviceSnapshotDir(projectId);
  const info = await FileSystem.getInfoAsync(dir);
  if (!info.exists) return [];
  try {
    const files = await FileSystem.readDirectoryAsync(dir);
    return files
      .map((f) => {
        const m = f.match(/^snapshot_v(\d+)\.enc$/);
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((v): v is number => v !== null)
      .sort((a, b) => b - a);
  } catch {
    return [];
  }
}

/** Delete a specific snapshot version from the device. */
export async function deleteSnapshot(projectId: number | string, version: number): Promise<void> {
  if (isWeb) {
    await webDeleteBlob(projectId, version);
    return;
  }
  const path = deviceSnapshotPath(projectId, version);
  const info = await FileSystem.getInfoAsync(path);
  if (info.exists) {
    await FileSystem.deleteAsync(path, { idempotent: true });
  }
}

// ─── iCloud storage ───────────────────────────────────────────────────────────

// The native iCloud module ships native iOS code; requiring it from JS lazily
// means test and Android builds never try to touch it. If the package is
// uninstalled the module resolves to null and every iCloud call short-circuits
// to "unavailable" without crashing.
type ICloudModule = {
  defaultICloudContainerPath: string | null;
  isICloudAvailableAsync: () => Promise<boolean>;
  isExistAsync: (path: string, isDirectory: boolean) => Promise<boolean>;
  createDirAsync: (path: string) => Promise<boolean>;
  uploadFileAsync: (args: { destinationPath: string; filePath: string }) => Promise<string>;
  downloadFileAsync: (path: string, destinationDir: string) => Promise<string>;
  unlinkAsync: (path: string) => Promise<boolean>;
  PathUtils?: {
    iCloudRemoveDotExt: (path: string) => string;
    ext: (path: string) => string | undefined;
  };
};

function getCloudStore(): ICloudModule | null {
  if (Platform.OS !== 'ios') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const mod = require('@oleg_svetlichnyi/expo-icloud-storage');
    return (mod?.default ?? mod) as ICloudModule;
  } catch {
    return null;
  }
}

export interface ICloudSnapshotMeta {
  /** Human-readable plan name surfaced as the Vault subfolder. */
  planLabel?: string;
  /** ISO timestamp of when the snapshot was created; embedded in the filename. */
  createdAt?: string;
}

/**
 * Upload the device copy of a snapshot into the app's iCloud container.
 * No-ops gracefully if iCloud is not available (not signed in, Android, etc).
 *
 * Writes to the user-facing path:
 *   Vault/{planLabel}/Snapshot v{N} — {Mon D, YYYY}.eleplan
 *
 * and ensures the Vault/README.txt explainer exists at the container root.
 */
export async function syncSnapshotToICloud(
  projectId: number | string,
  version: number,
  meta: ICloudSnapshotMeta = {},
): Promise<boolean> {
  const icloud = getCloudStore();
  if (!icloud) return false;
  try {
    const available = await icloud.isICloudAvailableAsync();
    if (!available) return false;

    const localPath = deviceSnapshotPath(projectId, version);
    const localInfo = await FileSystem.getInfoAsync(localPath);
    if (!localInfo.exists) return false;

    // Resolve createdAt — prefer the explicit value from the caller; fall back
    // to the envelope on disk. Having it before we compute the iCloud filename
    // is required because the date is part of the filename.
    let createdAt = meta.createdAt;
    if (!createdAt) {
      try {
        const content = await FileSystem.readAsStringAsync(localPath, {
          encoding: FileSystem.EncodingType.UTF8,
        });
        const envelope = JSON.parse(content) as SnapshotFileEnvelope;
        createdAt = envelope.created_at;
      } catch {
        // Fall through with undefined; formatVaultDate will render "Unknown Date"
        // rather than crashing.
      }
    }

    const planLabel = sanitizePlanLabel(meta.planLabel, projectId);

    // README at the container root is the "you're really backed up" proof the
    // user sees when they open Files.app. Write once, idempotent.
    await writeVaultReadmeIfMissing(icloud);

    // Ensure Vault/ and Vault/{planLabel}/ exist. createDirAsync is idempotent —
    // calling it for an existing path resolves rather than throwing.
    try {
      await icloud.createDirAsync(VAULT_ROOT);
    } catch (err: any) {
      debug.warn('iCloud createDirAsync(Vault) failed (continuing)', err?.message);
    }
    try {
      await icloud.createDirAsync(icloudVaultDir(planLabel));
    } catch (err: any) {
      debug.warn('iCloud createDirAsync(plan) failed (continuing)', err?.message);
    }

    await icloud.uploadFileAsync({
      destinationPath: icloudVaultSnapshotPath(planLabel, version, createdAt),
      filePath: localPath,
    });
    return true;
  } catch (err: any) {
    debug.warn('Failed to sync snapshot to iCloud', err?.message);
    return false;
  }
}

/**
 * Delete the iCloud copy of a specific snapshot version, if present. Tries the
 * new Vault/ path first, then the legacy `snapshots/project_{id}/` path so we
 * fully clean up after users who backed up before the rename.
 */
export async function deleteSnapshotFromICloud(
  projectId: number | string,
  version: number,
  meta: ICloudSnapshotMeta = {},
): Promise<void> {
  const icloud = getCloudStore();
  if (!icloud?.defaultICloudContainerPath) return;

  const containerDocs = `${icloud.defaultICloudContainerPath}/Documents/`;
  const planLabel = sanitizePlanLabel(meta.planLabel, projectId);

  const candidates = [
    icloudVaultSnapshotPath(planLabel, version, meta.createdAt),
    icloudLegacySnapshotPath(projectId, version),
  ];

  for (const rel of candidates) {
    try {
      await icloud.unlinkAsync(`${containerDocs}${rel}`);
    } catch (err: any) {
      // unlink of a non-existent file throws; that's expected when we try both
      // paths. Only log at debug level to keep the signal clean.
      debug.warn('iCloud unlinkAsync (best-effort)', err?.message);
    }
  }
}

// ─── Live backup status ───────────────────────────────────────────────────────

// How far back we're willing to scan through the registry looking for an
// older-but-verified copy. 5 covers the realistic "I backed up on my other
// device three times since I last opened this one" case without turning every
// status refresh into an O(all versions) scan.
const OUTDATED_SCAN_LIMIT = 5;

/**
 * Verify a single on-device snapshot file against its registered checksum.
 * Returns a tuple of (status, sizeBytes) rather than the full BackupStatus so
 * callers can compose the per-location verdict themselves. Never throws.
 */
async function verifyDeviceCopy(
  projectId: number | string,
  version: number,
  expectedChecksum: string,
): Promise<{ status: 'verified' | 'stale' | 'missing'; sizeBytes?: number }> {
  if (isWeb) {
    try {
      const info = await webBlobInfo(projectId, version);
      if (!info.exists) return { status: 'missing' };
      const content = await webLoadBlob(projectId, version);
      if (!content) return { status: 'missing' };
      const envelope = JSON.parse(content) as SnapshotFileEnvelope;
      const checksum = await envelopeChecksum(envelope);
      return {
        status: checksum === expectedChecksum ? 'verified' : 'stale',
        sizeBytes: info.sizeBytes,
      };
    } catch {
      return { status: 'missing' };
    }
  }
  const path = deviceSnapshotPath(projectId, version);
  try {
    const info = await FileSystem.getInfoAsync(path);
    if (!info.exists) return { status: 'missing' };
    const content = await FileSystem.readAsStringAsync(path, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    const envelope = JSON.parse(content) as SnapshotFileEnvelope;
    const checksum = await envelopeChecksum(envelope);
    const sizeBytes = new TextEncoder().encode(content).length;
    return {
      status: checksum === expectedChecksum ? 'verified' : 'stale',
      sizeBytes,
    };
  } catch {
    return { status: 'missing' };
  }
}

/**
 * Device-side backup status across the user's version registry.
 *
 * Walks in two passes:
 *   1. Check whether this device has a verified copy of the *latest* version.
 *      That's the happy path and returns `'verified'`.
 *   2. If latest is missing-or-corrupt, scan the local document dir for older
 *      verified copies. If any top-N registry version matches what's on disk,
 *      report `'outdated'` with the highest verified local version surfaced as
 *      `availableVersion`. The plan is still recoverable from this device —
 *      just not up-to-date.
 *
 * Corruption of the latest file is surfaced as `'stale'` even if older copies
 * exist, because the user needs to see the freshness problem (we still fill in
 * `availableVersion` with the best older copy as a hint).
 */
async function deviceStatus(
  projectId: number | string,
  registry: SnapshotRegistryEntry[],
): Promise<BackupStatus> {
  const now = new Date().toISOString();
  const latest = registry[0];
  if (!latest) {
    return { location: 'device', status: 'missing', checkedAt: now, availableVersion: null, targetVersion: null };
  }

  const latestCheck = await verifyDeviceCopy(projectId, latest.version, latest.checksum);
  if (latestCheck.status === 'verified') {
    return {
      location: 'device',
      status: 'verified',
      checkedAt: now,
      sizeBytes: latestCheck.sizeBytes,
      availableVersion: latest.version,
      targetVersion: latest.version,
    };
  }

  // Latest isn't cleanly present. Scan what IS on disk and see if any
  // registry entry matches. We intersect the on-disk version list with the
  // top-N registry so we don't re-read every old file on every refresh.
  let localVersions: number[] = [];
  try {
    localVersions = await listLocalSnapshotVersions(projectId);
  } catch {
    localVersions = [];
  }

  const candidateRegistry = registry.slice(0, OUTDATED_SCAN_LIMIT);
  let bestOlder: { version: number; sizeBytes?: number } | null = null;
  for (const entry of candidateRegistry) {
    if (entry.version === latest.version) continue;
    if (!localVersions.includes(entry.version)) continue;
    const check = await verifyDeviceCopy(projectId, entry.version, entry.checksum);
    if (check.status === 'verified') {
      bestOlder = { version: entry.version, sizeBytes: check.sizeBytes };
      break; // registry is version-descending, first hit is the best
    }
  }

  if (latestCheck.status === 'stale') {
    // Latest is on disk but corrupt. Surface the corruption explicitly; pass
    // the best older copy so the hero can offer "at least v{N} is intact".
    return {
      location: 'device',
      status: 'stale',
      checkedAt: now,
      sizeBytes: latestCheck.sizeBytes,
      availableVersion: bestOlder?.version ?? null,
      targetVersion: latest.version,
    };
  }

  if (bestOlder) {
    return {
      location: 'device',
      status: 'outdated',
      checkedAt: now,
      sizeBytes: bestOlder.sizeBytes,
      availableVersion: bestOlder.version,
      targetVersion: latest.version,
    };
  }

  return {
    location: 'device',
    status: 'missing',
    checkedAt: now,
    availableVersion: null,
    targetVersion: latest.version,
  };
}

/**
 * Checksum-verify an iCloud copy of a specific version. Downloads the file
 * into a temp dir, hashes it, then cleans up. Returns 'missing' if the file
 * isn't present or isn't yet downloadable (still syncing).
 */
async function verifyICloudCopy(
  icloud: ICloudModule,
  containerRoot: string,
  projectId: number | string,
  entry: SnapshotRegistryEntry,
  meta: ICloudSnapshotMeta,
): Promise<{ status: 'verified' | 'stale' | 'missing'; sizeBytes?: number }> {
  const planLabel = sanitizePlanLabel(meta.planLabel, projectId);
  // Prefer the current Vault layout; fall back to legacy for older users.
  // `.{filename}.icloud` placeholder files are iOS's "not yet downloaded"
  // marker and still count as "present in iCloud".
  const createdAt = entry.created_at ?? meta.createdAt;
  const newRelative = icloudVaultSnapshotPath(planLabel, entry.version, createdAt);
  const legacyRelative = icloudLegacySnapshotPath(projectId, entry.version);

  const newBasename = humanSnapshotFilename(entry.version, createdAt);
  const newPlaceholder = `${icloudVaultDir(planLabel)}/.${newBasename}.icloud`;
  const legacyPlaceholder = `${icloudLegacySnapshotDir(projectId)}/.snapshot_v${entry.version}.enc.icloud`;

  const [newReal, newPlace, legacyReal, legacyPlace] = await Promise.all([
    icloud.isExistAsync(newRelative, false).catch(() => false),
    icloud.isExistAsync(newPlaceholder, false).catch(() => false),
    icloud.isExistAsync(legacyRelative, false).catch(() => false),
    icloud.isExistAsync(legacyPlaceholder, false).catch(() => false),
  ]);

  const foundRelative =
    newReal || newPlace ? newRelative :
    legacyReal || legacyPlace ? legacyRelative :
    null;
  if (!foundRelative) return { status: 'missing' };

  const fullICloudPath = `${containerRoot}/Documents/${foundRelative}`;
  const tempDir = `${FileSystem.cacheDirectory}snapshot_icloud_verify/`;
  await ensureDir(tempDir);

  let localPath: string;
  try {
    localPath = await icloud.downloadFileAsync(fullICloudPath, tempDir);
  } catch (err: any) {
    debug.warn('iCloud downloadFileAsync failed', err?.message);
    // File reference exists but bytes aren't ready. Treat as missing for now;
    // a future refresh will try again once iCloud finishes syncing.
    return { status: 'missing' };
  }

  try {
    const content = await FileSystem.readAsStringAsync(localPath, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    const envelope = JSON.parse(content) as SnapshotFileEnvelope;
    const checksum = await envelopeChecksum(envelope);
    const sizeBytes = new TextEncoder().encode(content).length;
    return {
      status: checksum === entry.checksum ? 'verified' : 'stale',
      sizeBytes,
    };
  } catch {
    return { status: 'missing' };
  } finally {
    await FileSystem.deleteAsync(localPath, { idempotent: true }).catch(() => {});
  }
}

/**
 * iCloud-side backup status across the user's version registry.
 *
 * Same two-pass shape as `deviceStatus`: try the latest version first; if it
 * isn't verified-present, descend through the top-N registry entries until we
 * find an older verified copy (→ `'outdated'`) or exhaust the list (→
 * `'missing'`). Latest-file-with-corrupt-bytes surfaces as `'stale'`.
 */
async function icloudStatus(
  projectId: number | string,
  registry: SnapshotRegistryEntry[],
  meta: ICloudSnapshotMeta = {},
): Promise<BackupStatus> {
  const now = new Date().toISOString();
  const latest = registry[0] ?? null;
  const targetVersion = latest?.version ?? null;

  if (Platform.OS !== 'ios') {
    return { location: 'icloud', status: 'unavailable', checkedAt: now, availableVersion: null, targetVersion };
  }
  const icloud = getCloudStore();
  if (!icloud) {
    return { location: 'icloud', status: 'unavailable', checkedAt: now, availableVersion: null, targetVersion };
  }
  if (!latest) {
    return { location: 'icloud', status: 'missing', checkedAt: now, availableVersion: null, targetVersion: null };
  }

  try {
    const available = await icloud.isICloudAvailableAsync();
    const containerRoot = icloud.defaultICloudContainerPath;
    if (!available || !containerRoot) {
      return { location: 'icloud', status: 'unavailable', checkedAt: now, availableVersion: null, targetVersion };
    }

    const latestCheck = await verifyICloudCopy(icloud, containerRoot, projectId, latest, meta);
    if (latestCheck.status === 'verified') {
      return {
        location: 'icloud',
        status: 'verified',
        checkedAt: now,
        sizeBytes: latestCheck.sizeBytes,
        availableVersion: latest.version,
        targetVersion: latest.version,
      };
    }

    const candidateRegistry = registry.slice(0, OUTDATED_SCAN_LIMIT);
    let bestOlder: { version: number; sizeBytes?: number } | null = null;
    for (const entry of candidateRegistry) {
      if (entry.version === latest.version) continue;
      const check = await verifyICloudCopy(icloud, containerRoot, projectId, entry, meta);
      if (check.status === 'verified') {
        bestOlder = { version: entry.version, sizeBytes: check.sizeBytes };
        break;
      }
    }

    if (latestCheck.status === 'stale') {
      return {
        location: 'icloud',
        status: 'stale',
        checkedAt: now,
        sizeBytes: latestCheck.sizeBytes,
        availableVersion: bestOlder?.version ?? null,
        targetVersion: latest.version,
      };
    }
    if (bestOlder) {
      return {
        location: 'icloud',
        status: 'outdated',
        checkedAt: now,
        sizeBytes: bestOlder.sizeBytes,
        availableVersion: bestOlder.version,
        targetVersion: latest.version,
      };
    }
    return {
      location: 'icloud',
      status: 'missing',
      checkedAt: now,
      availableVersion: null,
      targetVersion: latest.version,
    };
  } catch (err: any) {
    debug.warn('iCloud status check errored', err?.message);
    return { location: 'icloud', status: 'missing', checkedAt: now, availableVersion: null, targetVersion };
  }
}

/**
 * Check the live backup status for all storage locations given the caller's
 * server-side registry of snapshot versions. The registry's first entry is
 * treated as the latest version, and each location reports whether it has
 * that version, a verified older version (`'outdated'`), or nothing at all.
 *
 * `meta.planLabel` and `meta.createdAt` let the iCloud lookup build the
 * user-facing filename; the per-entry `created_at` in the registry is
 * preferred when present (important when scanning older versions, each of
 * which has its own createdAt in the filename).
 *
 * On Android, the iCloud slot always returns `unavailable`.
 */
export async function checkBackupStatus(
  projectId: number | string,
  registry: SnapshotRegistryEntry[],
  meta: ICloudSnapshotMeta = {},
): Promise<BackupStatus[]> {
  const [device, icloud] = await Promise.all([
    deviceStatus(projectId, registry),
    icloudStatus(projectId, registry, meta),
  ]);
  return [device, icloud];
}

/**
 * Best-effort pull of the latest encrypted snapshot from iCloud down into the
 * device's local sandbox. This is the "curry the latest copy to new devices"
 * step — so a device that signs in after another device has already backed
 * up doesn't perpetually show as outdated on the Snapshots screen.
 *
 * Returns true if a copy landed in `${documentDirectory}snapshots/project_{id}`
 * during this call. False if iCloud doesn't have the file, iCloud is off, the
 * platform isn't iOS, or the download checksum didn't match (tampering/bit-
 * flip — we refuse to install a corrupt blob).
 *
 * The server never holds the ciphertext (see
 * `project_snapshot_versions` metadata-only table) so this is the ONLY path
 * by which a fresh device can auto-hydrate. If the user's other device used a
 * different iCloud account, hydration simply no-ops and the UI still reports
 * correctly via `checkBackupStatus` (the chip will read `'outdated'` or
 * `'missing'` with the right version numbers).
 */
export async function hydrateLatestFromICloud(
  projectId: number | string,
  registry: SnapshotRegistryEntry[],
  meta: ICloudSnapshotMeta = {},
): Promise<boolean> {
  if (Platform.OS !== 'ios') return false;
  const latest = registry[0];
  if (!latest) return false;

  const icloud = getCloudStore();
  if (!icloud) return false;

  try {
    const available = await icloud.isICloudAvailableAsync();
    const containerRoot = icloud.defaultICloudContainerPath;
    if (!available || !containerRoot) return false;

    // Don't do anything if the device already has a verified latest.
    const already = await verifyDeviceCopy(projectId, latest.version, latest.checksum);
    if (already.status === 'verified') return false;

    const planLabel = sanitizePlanLabel(meta.planLabel, projectId);
    const createdAt = latest.created_at ?? meta.createdAt;
    const newRelative = icloudVaultSnapshotPath(planLabel, latest.version, createdAt);
    const legacyRelative = icloudLegacySnapshotPath(projectId, latest.version);

    const newBasename = humanSnapshotFilename(latest.version, createdAt);
    const newPlaceholder = `${icloudVaultDir(planLabel)}/.${newBasename}.icloud`;
    const legacyPlaceholder = `${icloudLegacySnapshotDir(projectId)}/.snapshot_v${latest.version}.enc.icloud`;

    const [newReal, newPlace, legacyReal, legacyPlace] = await Promise.all([
      icloud.isExistAsync(newRelative, false).catch(() => false),
      icloud.isExistAsync(newPlaceholder, false).catch(() => false),
      icloud.isExistAsync(legacyRelative, false).catch(() => false),
      icloud.isExistAsync(legacyPlaceholder, false).catch(() => false),
    ]);
    const foundRelative =
      newReal || newPlace ? newRelative :
      legacyReal || legacyPlace ? legacyRelative :
      null;
    if (!foundRelative) return false;

    const fullICloudPath = `${containerRoot}/Documents/${foundRelative}`;
    const tempDir = `${FileSystem.cacheDirectory}snapshot_icloud_hydrate/`;
    await ensureDir(tempDir);

    let downloadedPath: string;
    try {
      downloadedPath = await icloud.downloadFileAsync(fullICloudPath, tempDir);
    } catch (err: any) {
      debug.warn('hydrate: downloadFileAsync failed', err?.message);
      return false;
    }

    try {
      const content = await FileSystem.readAsStringAsync(downloadedPath, {
        encoding: FileSystem.EncodingType.UTF8,
      });
      const envelope = JSON.parse(content) as SnapshotFileEnvelope;
      const checksum = await envelopeChecksum(envelope);
      if (checksum !== latest.checksum) {
        // The iCloud copy doesn't match the registry. Refuse to install it —
        // better to show "outdated" than silently cache a corrupt blob.
        debug.warn('hydrate: checksum mismatch, refusing to install', {
          expected: latest.checksum,
          got: checksum,
        });
        return false;
      }

      await ensureDir(deviceSnapshotDir(projectId));
      const dest = deviceSnapshotPath(projectId, latest.version);
      await FileSystem.writeAsStringAsync(dest, content, {
        encoding: FileSystem.EncodingType.UTF8,
      });
      return true;
    } catch (err: any) {
      debug.warn('hydrate: read/parse/write failed', err?.message);
      return false;
    } finally {
      await FileSystem.deleteAsync(downloadedPath, { idempotent: true }).catch(() => {});
    }
  } catch (err: any) {
    debug.warn('hydrate: unexpected error', err?.message);
    return false;
  }
}
